
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToast } from '../../components/common/ToastContext';
import type { Message, CommunicationTemplate } from '@repo/shared-types/communication';
import { threadSchema, messageSchema, notificationSchema, smsTemplateSchema } from '@repo/shared-types/communication/schemas';
import { validateEntityArray } from '@repo/shared-utils';
import { logger } from '@repo/shared-utils';

export const useThreads = () => {
    return useQuery({
        queryKey: ['communications', 'threads'],
        queryFn: async () => {
            const data = await api.communications.threads.list();
            // ✅ Validate query response with Zod schema
            const validation = validateEntityArray(threadSchema, data);
            if (!validation.valid) {
                logger.error('Invalid threads query response:', validation.errors);
                throw new Error('Invalid threads data received from API');
            }
            // Type assertion: Zod-validated data matches Thread type structure
            // Component may add computed properties (participantName, lastMessageTime, lastMessage)
            return validation.data! as Array<import('@repo/shared-types/communication').Thread & { participantName?: string; lastMessageTime?: string; lastMessage?: string }>;
        },
        staleTime: 1000 * 30, // 30s poll
    });
};

export const useMessages = (threadId?: string) => {
    return useQuery({
        queryKey: ['communications', 'messages', threadId],
        queryFn: async () => {
            const msgs = await api.communications.messages.list();
            // ✅ Validate query response with Zod schema
            const validation = validateEntityArray(messageSchema, msgs);
            if (!validation.valid) {
                logger.error('Invalid messages query response:', validation.errors);
                throw new Error('Invalid messages data received from API');
            }
            const validatedMessages = validation.data! as Message[];
            if (threadId) return validatedMessages.filter((m) => m.threadId === threadId);
            return validatedMessages;
        },
        enabled: true  // Always fetch messages (filtered by threadId if provided)
    });
};

export const useSendMessage = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (msg: Omit<Message, 'id' | 'timestamp' | 'read'>) => {
            const messageWithDefaults: Omit<Message, 'id'> = {
                ...msg,
                timestamp: new Date().toISOString(),
                read: false
            };
            return api.communications.messages.create(messageWithDefaults);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['communications'] });
            showToast('Message sent', 'success');
        }
    });
};

export const useUpdateMessage = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: Partial<Message> }) =>
            api.communications.messages.update(id, updates),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['communications'] });
            showToast('Message updated', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to update message', 'error');
        }
    });
};

export const useDeleteMessage = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (id: string) => api.communications.messages.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['communications'] });
            showToast('Message deleted', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to delete message', 'error');
        }
    });
};

// --- NOTIFICATIONS ---

export const useNotifications = () => {
    return useQuery({
        queryKey: ['notifications'],
        queryFn: async () => {
            const data = await api.communications.notifications.list();
            // ✅ Validate query response with Zod schema
            const validation = validateEntityArray(notificationSchema, data);
            if (!validation.valid) {
                logger.error('Invalid notifications query response:', validation.errors);
                throw new Error('Invalid notifications data received from API');
            }
            return validation.data! as import('@repo/shared-types/communication').AppNotification[];
        },
        refetchInterval: 1000 * 60, // Poll every minute
    });
};

export const useMarkNotificationRead = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => api.communications.notifications.markRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
    });
};

export const useClearNotifications = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => api.communications.notifications.clearAll(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
    });
};

// --- EMAIL TEMPLATES ---

export const useEmailTemplates = () => {
    return useQuery({
        queryKey: ['communications', 'email', 'templates'],
        queryFn: () => api.communications.email.templates.list(),
    });
};

export const useCreateEmailTemplate = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (template: Omit<import('@repo/shared-types/communication').CommunicationTemplate, 'id' | 'createdAt' | 'updatedAt'>) => api.communications.email.templates.create(template),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['communications', 'email', 'templates'] });
            showToast('Email template created successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to create template', 'error');
        }
    });
};

export const useUpdateEmailTemplate = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: Partial<import('@repo/shared-types/communication').CommunicationTemplate> }) =>
            api.communications.email.templates.update(id, updates),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['communications', 'email', 'templates'] });
            showToast('Email template updated successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to update template', 'error');
        }
    });
};

export const useDeleteEmailTemplate = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (id: string) => api.communications.email.templates.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['communications', 'email', 'templates'] });
            showToast('Email template deleted successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to delete template', 'error');
        }
    });
};

// --- SMS TEMPLATES ---

export const useSMSTemplates = () => {
    return useQuery({
        queryKey: ['communications', 'sms', 'templates'],
        queryFn: async () => {
            const data = await api.communications.sms.templates.list();
            // ✅ Validate query response with Zod schema
            const validation = validateEntityArray(smsTemplateSchema, data);
            if (!validation.valid) {
                logger.error('Invalid SMS templates query response:', validation.errors);
                throw new Error('Invalid SMS templates data received from API');
            }
            const templates = validation.data! as Array<{
                id: string;
                name: string;
                message?: string;
                body?: string;
                variables?: string[];
                createdAt?: string;
                updatedAt?: string;
            }>;
            return templates.map((t) => ({
                id: t.id,
                name: t.name,
                subject: t.name, // Use name as subject for SMS
                content: t.message || t.body || '',
                category: 'General',
                type: 'sms',
                variables: t.variables || [],
                createdAt: t.createdAt,
                updatedAt: t.updatedAt
            })) as CommunicationTemplate[];
        },
    });
};

export const useCreateSMSTemplate = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (template: Omit<import('@repo/shared-types/communication').CommunicationTemplate, 'id' | 'createdAt' | 'updatedAt'>) => api.communications.sms.templates.create(template),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['communications', 'sms', 'templates'] });
            showToast('SMS template created successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to create template', 'error');
        }
    });
};

export const useUpdateSMSTemplate = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: Partial<import('@repo/shared-types/communication').CommunicationTemplate> }) =>
            api.communications.sms.templates.update(id, updates),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['communications', 'sms', 'templates'] });
            showToast('SMS template updated successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to update template', 'error');
        }
    });
};

export const useDeleteSMSTemplate = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (id: string) => api.communications.sms.templates.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['communications', 'sms', 'templates'] });
            showToast('SMS template deleted successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to delete template', 'error');
        }
    });
};


